import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SharedReduxState } from '../../types';
import { COMPANY_SETTING_NAME } from '../../../../../frontend/global/modules/companies/api/company-settings/enums/company-settings';
import { CompanyWorkspacePortal } from '../../../api/types/UserCompany.types';
import { COMPANY_ACCESS_RULE } from '../../../../../frontend/global/modules/companies/api/enums/company-access-rule';

export const useUserCompanies = () => {
  const userCompanies = useSelector(
    (state: SharedReduxState) => state.userCompanies
  );

  const isCompanySettingEnabled = (settingName: COMPANY_SETTING_NAME) => {
    if (!userCompanies?.current) return false;
    const setting = userCompanies.current.settings.find(setting => setting.name === settingName);
    return !!setting?.value;
  };

  const companyAvailablePortals = useMemo(() => {
    if (!userCompanies?.current) return [];
    return userCompanies?.current?.workspaces.reduce((res, workspace) => {
      return [...res, ...workspace.portals];
    }, [] as CompanyWorkspacePortal[]);
  }, [userCompanies?.current]);

  const allAvailablePortals: CompanyWorkspacePortal[] = useMemo(() => {
    if (!userCompanies?.companies) return [];
    const uniquePortalsMap = userCompanies?.companies.reduce((res, company) => {
      return company.workspaces.reduce((res, workspace) => {
        workspace.portals.forEach(portal => {
          if (!res.has(portal.id)) res.set(portal.id, portal);
        });
        return res;
      }, res);
    }, new Map());

    return [...uniquePortalsMap.values()];

  }, [userCompanies?.companies]);

  const getCurrentCompanyPortalsByAccessRule = (portalAccessRules: COMPANY_ACCESS_RULE[]) => {
    return companyAvailablePortals.filter(portal => portalAccessRules.includes(portal.access_rule));
  };

  return {
    current: userCompanies?.current,
    companies: userCompanies?.companies,
    companyAvailablePortals,
    allAvailablePortals,
    getCurrentCompanyPortalsByAccessRule,
    isCompanySettingEnabled
  };
};